/**
 * 货币相关工具函数
 */

/**
 * 获取货币符号
 * @param {string} currency - 货币代码
 * @returns {string} 货币符号
 */
export const getCurrencySymbol = (currency) => {
  const symbols = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'KRW': '₩',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'INR': '₹',
    'SGD': 'S$',
    'HKD': 'HK$',
    'TWD': 'NT$',
    'THB': '฿',
    'MYR': 'RM',
    'PHP': '₱',
    'IDR': 'Rp',
    'VND': '₫',
    'BRL': 'R$',
    'MXN': 'MX$',
    'ARS': 'AR$',
    'CLP': 'CL$',
    'COP': 'CO$',
    'PEN': 'S/',
    'TRY': '₺',
    'ILS': '₪',
    'SAR': 'SR',
    'AED': 'AED',
    'EGP': 'E£',
    'ZAR': 'R',
    'NGN': '₦',
    'KES': 'KSh',
    'GHS': 'GH₵',
    'MAD': 'MAD',
    'TND': 'TND',
    'XOF': 'CFA',
    'XAF': 'FCFA'
  }
  return symbols[currency] || currency
}

/**
 * 格式化价格（原始货币）
 * @param {number|string} price - 价格
 * @param {string} currency - 货币代码
 * @returns {string} 格式化后的价格字符串
 */
export const formatPrice = (price, currency) => {
  const symbol = getCurrencySymbol(currency)
  const priceNum = typeof price === 'string' ? parseFloat(price) : price
  if (priceNum == null || isNaN(priceNum)) {
    return `${symbol}0.00`
  }
  return `${symbol}${priceNum.toFixed(2)}`
}

/**
 * 格式化人民币价格
 * @param {number|string} price - 价格
 * @param {number} exchangeRate - 汇率
 * @returns {string} 格式化后的人民币价格
 */
export const formatPriceRMB = (price, exchangeRate) => {
  const priceNum = typeof price === 'string' ? parseFloat(price) : price
  if (priceNum == null || isNaN(priceNum)) {
    return `¥0.00`
  }
  const rmb = priceNum * (exchangeRate || 1)
  return `¥${rmb.toFixed(2)}`
}

/**
 * 格式化带人民币参考的价格
 * @param {number|string} price - 价格
 * @param {string} currency - 货币代码
 * @param {number} exchangeRate - 汇率
 * @returns {object} 包含原始价格和人民币价格的对象
 */
export const formatPriceWithRMB = (price, currency, exchangeRate) => {
  const priceNum = typeof price === 'string' ? parseFloat(price) : price
  const validPriceNum = (priceNum == null || isNaN(priceNum)) ? 0 : priceNum
  const validExchangeRate = exchangeRate || 1
  return {
    original: formatPrice(price, currency),
    rmb: formatPriceRMB(price, exchangeRate),
    priceNum: validPriceNum,
    rmbNum: validPriceNum * validExchangeRate
  }
}

/**
 * 计算总价
 * @param {Array} items - 商品列表
 * @param {string} priceField - 价格字段名，默认为'price'
 * @param {string} quantityField - 数量字段名，默认为'quantity'
 * @returns {number} 总价
 */
export const calculateTotal = (items, priceField = 'price', quantityField = 'quantity') => {
  return items.reduce((total, item) => {
    const price = typeof item[priceField] === 'string' ? parseFloat(item[priceField]) : item[priceField]
    const quantity = item[quantityField] || 1
    const validPrice = (price == null || isNaN(price)) ? 0 : price
    return total + (validPrice * quantity)
  }, 0)
}

/**
 * 计算人民币总价
 * @param {Array} items - 商品列表
 * @param {number} exchangeRate - 汇率
 * @param {string} priceField - 价格字段名
 * @param {string} quantityField - 数量字段名
 * @returns {number} 人民币总价
 */
export const calculateTotalRMB = (items, exchangeRate, priceField = 'price', quantityField = 'quantity') => {
  const total = calculateTotal(items, priceField, quantityField)
  return total * exchangeRate
}

export default {
  getCurrencySymbol,
  formatPrice,
  formatPriceRMB,
  formatPriceWithRMB,
  calculateTotal,
  calculateTotalRMB
}